import React, { useState, useEffect, useRef, useCallback } from 'react';
import Splash from '../../../../splash/Splash';

// Debug flag - set to false for production
const DEBUG = process.env.NODE_ENV === 'development';

function APIS({ setGlobalData, refreshPvtData }) {
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
    const [isLoadingPublic, setIsLoadingPublic] = useState(true);
    const [isLoadingPrivate, setIsLoadingPrivate] = useState(isLoggedIn);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);
    const prevApiDataRef = useRef(null);

    // Define API data states
    const [apiData, setApiData] = useState({
        sliders: { main: [], favourite: [], feature: [] },
        socialLinks: [],
        general: {},
        paymentMethods: [],
        withdrawMethods: [],
        hasEWallet: false,
        promotions: { activePromotion: [], displayPromotion: [], allPromotions: [] },
        userData: null,
        transactions: [],
        turnovers: [],
        marketBlock: [],
    });

    // Fetch Public APIs (No login required)
    useEffect(() => {
        mountedRef.current = true;
        const fetchPublicData = async () => {
            if (DEBUG) console.time('Public API Fetch Time');
            setIsLoadingPublic(true);

            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                let hasEWallet = false;

                const apiResponses = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/api/sliders`, { headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/sociallink`, { headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/general`, { headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/payment-methods`, { headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/promotion`, { headers }).then(res => res.json()),
                ]);

                const [slidersData, socialData, generalData, paymentData, promotionsData] = apiResponses;

                let pgs = [];
                if (paymentData?.type === 'ewallet') {
                    pgs = paymentData?.pgs;
                    hasEWallet = true;
                } else {
                    pgs = paymentData;
                    hasEWallet = false;
                }

                if (generalData && generalData[0]) {
                    document.title = generalData[0].site_title;
                    localStorage.setItem('whatsapp', generalData[0].whatsapp);
                    localStorage.setItem('liveChat', generalData[0].live_chat);
                    localStorage.setItem('intercom', generalData[0].intercom);
                    if (generalData[0]?.tawk_id) localStorage.setItem('tawkId', generalData[0].tawk_id);
                }

                if (mountedRef.current) {
                    setApiData(prevData => ({
                        ...prevData,
                        hasEWallet,
                        sliders: {
                            main: slidersData.filter(s => s.status === 'active' && s.slider_type === 'Main'),
                            favourite: slidersData.filter(s => s.status === 'active' && s.slider_type === 'Favourite'),
                            feature: slidersData.filter(s => s.status === 'active' && s.slider_type === 'Features'),
                        },
                        socialLinks: socialData[0] || {},
                        general: generalData[0] || {},
                        paymentMethods: pgs,
                        promotions: {
                            activePromotion: promotionsData.filter(p => p.status === 'active'),
                            displayPromotion: promotionsData.filter(p => p.status === 'display'),
                            allPromotions: promotionsData,
                        },
                    }));
                }
            } catch (err) {
                if (DEBUG) console.error('Error fetching public data:', err);
                if (mountedRef.current) setError('Error fetching public API data');
            } finally {
                if (mountedRef.current) setIsLoadingPublic(false);
                if (DEBUG) console.timeEnd('Public API Fetch Time');
            }
        };

        fetchPublicData();
        return () => { mountedRef.current = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Private APIs (Login required)
    useEffect(() => {
        mountedRef.current = true;
        const token = localStorage.getItem('token');
        const remoteId = localStorage.getItem('remote_id');

        if (isLoggedIn && token && remoteId) {
            if (DEBUG) console.time('Private API Fetch Time');
            setIsLoadingPrivate(true);

            const fetchPrivateData = async () => {
                try {
                    const apiResponses = await Promise.all([
                        fetch(`${process.env.REACT_APP_API_URL}/api/user/show?remote_id=${remoteId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/transaction/show?remote_id=${remoteId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/turnovers?remote_id=${remoteId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/market/block`, {
                            headers: { Authorization: `Bearer ${token}` },
                        }).then(res => res.json()),
                    ]);

                    const [userData, transactions, turnovers, marketBlock] = apiResponses;

                    if (mountedRef.current) {
                        setApiData(prevData => ({
                            ...prevData,
                            userData,
                            transactions,
                            turnovers: turnovers?.turnover || [],
                            marketBlock: marketBlock || [],
                        }));
                    }
                } catch (err) {
                    if (DEBUG) console.error('Error fetching private data:', err);
                    if (mountedRef.current) setError('Error fetching user data');
                } finally {
                    if (mountedRef.current) setIsLoadingPrivate(false);
                    if (DEBUG) console.timeEnd('Private API Fetch Time');
                }
            };

            fetchPrivateData();
        }
        return () => { mountedRef.current = false; };
    }, [isLoggedIn, refreshPvtData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pass API data to global state in App.js — only when data actually changes
    useEffect(() => {
        const dataStr = JSON.stringify(apiData);
        if (prevApiDataRef.current !== dataStr) {
            prevApiDataRef.current = dataStr;
            setGlobalData(apiData);
        }
    }, [apiData, setGlobalData]);

    // Loading State Handling
    if (isLoadingPublic) {
        return <Splash />;
    }

    return null;
}

export default APIS;
